import errors from '@front/volcanion/classes/error'
import { I18n } from '@front/volcanion'
import FormatUtils from '@front/squirtle/utils/format'
import _ from 'lodash'
import moment from 'moment'

import { calculateTimeDelay } from './utils'

class Callbacks {
  static onNextHandler(openDialog, order, next_transport_status, updateStatus, max_start_planned_order) {
    return async function onNext(values, extra, meta, state) {
      const assigned_transport = order?.assigned_transport
      const disable_notifications = values?.disable_notifications
      const disabled_notification_channels = disable_notifications ? ['customer', 'passenger'] : []
      const deferTo = order?.job?.deferTo
      const minStart = !!deferTo && _.isFinite(max_start_planned_order)
        ? moment.utc(deferTo).subtract(max_start_planned_order, 'minutes')
        : null
      const isEarlyStart = assigned_transport?.status === 'planned'
        && !!minStart
        && moment.utc().isBefore(minStart)

      const formPayload = {
        transport_id: assigned_transport?.transport_id,
        status: next_transport_status,
        extra: {
          load_count: order?.passenger_count,
          passenger_name: order?.loads?.[0]?.src_contact,
        },
        force: true,
        disabled_notification_channels
      }

      if (isEarlyStart)
        return openDialog('confirmation_dialog', {
          title: I18n.t('transport.popup.early_start.title', {
            requestedAt_date: FormatUtils.formatDateBackToFront(order?.requestedAt, 'DD/MM/YYYY'),
            requestedAt_time: FormatUtils.formatDateBackToFront(order?.requestedAt, 'HH[h]mm')
          }),
          description: I18n.t('transport.popup.early_start.description', {
            minStart_date: FormatUtils.formatDateBackToFront(minStart, 'DD/MM/YYYY'),
            minStart_time: FormatUtils.formatDateBackToFront(minStart, 'HH[h]mm')
          }),
          onConfirm: (data) => updateStatus(data)
        }, formPayload)

      return openDialog('confirmation_dialog', {
        title: I18n.t(`transport.popup.${next_transport_status}.title`),
        description: _.compact([
          I18n.t(`transport.popup.${next_transport_status}.description`),
          !!disable_notifications ? null : I18n.t(`transport.informed_client`)
        ]).join(` ${I18n.t('prefix.and')} `),
        onConfirm: (data) => updateStatus(data)
      }, formPayload)
    }
  }

  static onPaymentHandler(openDialog, order, payJob) {
    return async function onPayment(values, extra, meta, state) {
      const assigned_transport = order?.assigned_transport
      const disable_notifications = values?.disable_notifications
      const disabled_notification_channels = disable_notifications ? ['customer', 'passenger'] : []
      return openDialog('confirmation_dialog', {
        title: I18n.t(`transport.popup.complete.title`),
        description: I18n.t(`transport.popup.complete.description`),
        onConfirm: (data) => payJob(data)
      }, {
        user_id: assigned_transport?.driver,
        job_id: order?.job?.job_id,
        disabled_notification_channels,
        ...values,
      })
    }
  }

  static onCancelHandler(openDialog, order, orderCancel, not_loaded_reason_id) {
    return async function onCancel(values, extra, meta, state) {
      const disable_notifications = values?.disable_notifications
      const disabled_notification_channels = disable_notifications ? ['customer', 'passenger'] : []
      return openDialog('confirmation_dialog', {
        title: I18n.t(`transport.popup.not_loaded.title`),
        description: _.compact([
          I18n.t(`transport.popup.not_loaded.description`),
          !!disable_notifications ? null : I18n.t(`transport.informed_client`)
        ]).join(` ${I18n.t('prefix.and')} `),
        onConfirm: (data) => orderCancel(data)
      }, {
        order_id: order?.order_id,
        reason: not_loaded_reason_id,
        disabled_notification_channels
      })
    }
  }

  static onCancelRedispatchHandler(openDialog, order, cancelQueueJob, getQueueJob) {
    return async function onCancelRedispatch(values, extra, meta, state) {
      return openDialog('confirmation_dialog', {
        title: I18n.t(`transport.popup.cancel_redispatch.title`),
        description: I18n.t(`transport.popup.cancel_redispatch.description`),
        onConfirm: async (data) => {
          await cancelQueueJob({ ...data, name: 'auto_redispatch' })
          await cancelQueueJob({ ...data, name: 'dispatch_reminder' })
          await getQueueJob({ ...data, name: 'auto_redispatch' })
        }
      }, {
        job_id: order?.job?.job_id
      })
    }
  }

  static onEnableRedispatchHandler(openDialog, order, createQueueJob, getQueueJob, auto_redispatch_delay, dispatch_reminder_delay) {
    return async function onEnableRedispatch(values, extra, meta, state) {
      return openDialog('confirmation_dialog', {
        title: I18n.t(`transport.popup.enable_redispatch.title`),
        description: I18n.t(`transport.popup.enable_redispatch.description`),
        onConfirm: async (data) => {
          await createQueueJob({
            ...data,
            name: 'auto_redispatch',
            delay: calculateTimeDelay(moment.utc(), order?.job?.deferTo, auto_redispatch_delay)
          })
          await createQueueJob({
            ...data,
            name: 'dispatch_reminder',
            delay: calculateTimeDelay(moment.utc(), order?.job?.deferTo, dispatch_reminder_delay)
          })
          await getQueueJob({ ...data, name: 'auto_redispatch' })
        }
      }, {
        job_id: order?.job?.job_id,
      })
    }
  }

  static onSubmitHandler(onNext, onPayment, onCancel, onCancelRedispatch, onEnableRedispatch) {
    return async function onSubmit(values, extra, meta, state) {
      switch (extra?.submitType) {
        case 'next':
          return onNext(values, extra, meta, state)
        case 'payment':
          return onPayment(values, extra, meta, state)
        case 'cancel':
          return onCancel(values, extra, meta, state)
        case 'cancel_redispatch':
          return onCancelRedispatch(values, extra, meta, state)
        case 'enable_redispatch':
          return onEnableRedispatch(values, extra, meta, state)
        default:
          throw new errors.ArgumentError("Invalid submit type in simulator", extra)
      }
    }
  }

  static getFailedNotificationHandler() {
    return function getFailedNotification(error, values, extra, meta, state) {
      const context = error.getContext()
      const message = (error instanceof errors.MismatchError && context?.data?.message) || I18n.t('error.unknow')
      return [message, { variant: 'error' }]
    }
  }
}

export default Callbacks
